import { useEffect, useState } from "react";
import {
    Phone,
    Clock,
    MapPin,
    Shield,
    Plane,
    Briefcase,
    Users,
    Package,
    Car,
    Star,
    Navigation,
    MessageCircle,
    ChevronRight,
} from "lucide-react";
import { TAXI } from "@/constants/testIds";

const PHONE_DISPLAY = "0532 521 02 86";
const PHONE_TEL = "+905325210286";
const PHONE_WA = "905325210286";

const services = [
    {
        icon: Plane,
        title: "Havalimanı Transfer",
        desc: "İstanbul Sabiha Gökçen ve İstanbul Havalimanı'na konforlu transfer.",
    },
    {
        icon: Car,
        title: "Şehirler Arası Taksi",
        desc: "Tekirdağ, İstanbul ve Çorlu hatlarında 7/24 kesintisiz hizmet.",
    },
    {
        icon: Briefcase,
        title: "Kurumsal Hizmet",
        desc: "Fabrika servisleri, toplantı ve iş transferleri için kurumsal çözümler.",
    },
    {
        icon: Users,
        title: "VIP Müşteri Taşıma",
        desc: "Düğün, nişan ve özel günlerinizde özenli, temiz ve hızlı taksi.",
    },
    {
        icon: Package,
        title: "Paket / Kargo",
        desc: "Acil belge ve paket teslimatları için hızlı kurye taksi hizmeti.",
    },
    {
        icon: Shield,
        title: "Güvenli Yolculuk",
        desc: "Lisanslı, deneyimli şoförler ve düzenli bakımlı araç filosu.",
    },
];

const areas = [
    "Yeniçiftlik",
    "Marmaraereğlisi",
    "Tekirdağ",
    "Çorlu",
    "İstanbul",
    "Silivri",
    "Çerkezköy",
    "Kapaklı",
    "Sultanbeyli",
    "Sabiha Gökçen",
    "İstanbul Havalimanı",
    "Süleymanpaşa",
];

const marqueeRoutes = [
    "Yeniçiftlik → İstanbul",
    "Yeniçiftlik → Sabiha Gökçen",
    "Yeniçiftlik → İstanbul Havalimanı",
    "Yeniçiftlik → Tekirdağ",
    "Yeniçiftlik → Çorlu",
    "Yeniçiftlik → Marmaraereğlisi",
    "Yeniçiftlik → Silivri",
    "Yeniçiftlik → Çerkezköy",
];

export default function TaxiLanding() {
    const [scrolled, setScrolled] = useState(false);
    const year = new Date().getFullYear();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const call = () => {
        window.location.href = `tel:${PHONE_TEL}`;
    };

    return (
        <div className="min-h-screen bg-[var(--kt-paper)] text-[var(--kt-ink)]">
            {/* Top stripe */}
            <div className="checker-thin h-3 w-full" aria-hidden="true" />

            {/* Header */}
            <header
                className={`sticky top-0 z-40 w-full border-b-2 border-[var(--kt-ink)] transition-all ${
                    scrolled
                        ? "bg-[var(--kt-paper)]/95 backdrop-blur-md"
                        : "bg-[var(--kt-paper)]"
                }`}
            >
                <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
                    <a
                        href="#hero"
                        data-testid={TAXI.headerLogo}
                        className="flex items-center gap-2"
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--kt-yellow)] border-2 border-[var(--kt-ink)]">
                            <Car className="h-5 w-5" strokeWidth={2.5} />
                        </span>
                        <span className="font-display text-base font-extrabold leading-tight sm:text-lg">
                            Yeniçiftlik
                            <span className="block text-xs font-semibold tracking-widest uppercase text-[var(--kt-ink-soft)]">
                                Köşe Taksi
                            </span>
                        </span>
                    </a>

                    <nav className="hidden items-center gap-6 text-sm font-semibold lg:flex">
                        <a data-testid={TAXI.navServices} href="#services" className="hover:text-[var(--kt-ink-soft)]">
                            Hizmetler
                        </a>
                        <a data-testid={TAXI.navAreas} href="#areas" className="hover:text-[var(--kt-ink-soft)]">
                            Bölgeler
                        </a>
                        <a data-testid={TAXI.navAbout} href="#about" className="hover:text-[var(--kt-ink-soft)]">
                            Hakkımızda
                        </a>
                        <a data-testid={TAXI.navLocation} href="#location" className="hover:text-[var(--kt-ink-soft)]">
                            Konum
                        </a>
                        <a data-testid={TAXI.navContact} href="#contact" className="hover:text-[var(--kt-ink-soft)]">
                            İletişim
                        </a>
                    </nav>

                    <button
                        data-testid={TAXI.headerCallBtn}
                        onClick={call}
                        className="btn-yellow inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold sm:px-5"
                    >
                        <Phone className="h-4 w-4" strokeWidth={2.5} />
                        <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
                        <span className="sm:hidden">Ara</span>
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section
                id="hero"
                data-testid={TAXI.navHero}
                className="relative overflow-hidden bg-[var(--kt-ink)] text-[var(--kt-paper)] grain"
            >
                <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[var(--kt-yellow)] opacity-90 blur-[1px]" />
                <div className="pointer-events-none absolute right-24 top-44 h-2 w-40 rotate-[18deg] bg-[var(--kt-yellow)]" />

                <div className="relative mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[1.15fr_1fr] lg:py-28">
                    <div className="relative z-10">
                        <span className="rise rise-1 inline-flex items-center gap-2 rounded-full border border-[var(--kt-yellow)]/40 bg-[var(--kt-yellow)]/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[var(--kt-yellow)]">
                            <Clock className="h-3.5 w-3.5" />
                            7 / 24 Kesintisiz Hizmet
                        </span>

                        <h1 className="rise rise-2 font-display mt-5 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
                            Yeniçiftlik'in
                            <br />
                            <span className="bg-[var(--kt-yellow)] px-2 text-[var(--kt-ink)]">
                                Köşe Taksi
                            </span>
                            'si.
                            <br />
                            Bir telefon yeter.
                        </h1>

                        <p className="rise rise-3 mt-6 max-w-xl text-base leading-relaxed text-[var(--kt-paper)]/80 sm:text-lg">
                            Marmaraereğlisi <strong className="text-[var(--kt-yellow)]">Yeniçiftlik</strong> merkezli,
                            <strong> İstanbul</strong>, <strong>Tekirdağ</strong> ve <strong>Çorlu</strong> hatlarında
                            güvenli, hızlı ve temiz taksi hizmeti. Lisanslı şoförler, sabit fiyat, 7/24 ulaşılabilir.
                        </p>

                        <div className="rise rise-4 mt-8 flex flex-wrap items-center gap-3">
                            <button
                                data-testid={TAXI.heroCallBtn}
                                onClick={call}
                                className="btn-yellow inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-bold"
                            >
                                <Phone className="h-5 w-5" strokeWidth={2.6} />
                                Hemen Ara · {PHONE_DISPLAY}
                            </button>
                            <a
                                data-testid={TAXI.heroWhatsappBtn}
                                href={`https://wa.me/${PHONE_WA}?text=${encodeURIComponent(
                                    "Merhaba, Yeniçiftlik Köşe Taksi'den taksi rica ediyorum.",
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--kt-yellow)] px-5 py-3 text-base font-bold text-[var(--kt-yellow)] transition hover:bg-[var(--kt-yellow)] hover:text-[var(--kt-ink)]"
                            >
                                <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                                WhatsApp
                            </a>
                        </div>

                        <div className="rise rise-4 mt-10 grid max-w-md grid-cols-3 gap-4 text-center">
                            {[
                                { k: "7/24", v: "Hizmet" },
                                { k: "3+", v: "Şehir Hattı" },
                                { k: "★ 5.0", v: "Müşteri" },
                            ].map((s) => (
                                <div key={s.k} className="rounded-xl border border-[var(--kt-yellow)]/30 bg-white/5 px-3 py-3 backdrop-blur-sm">
                                    <div className="font-display text-xl font-extrabold text-[var(--kt-yellow)] sm:text-2xl">
                                        {s.k}
                                    </div>
                                    <div className="text-xs uppercase tracking-wider text-[var(--kt-paper)]/70">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Hero card */}
                    <div className="relative z-10 lg:pl-6">
                        <div className="rise rise-3 relative mx-auto max-w-md rounded-2xl border-2 border-[var(--kt-yellow)] bg-[var(--kt-ink-soft)] p-6 sm:p-7 shadow-[10px_10px_0_0_var(--kt-yellow)]">
                            <div className="flex items-center gap-3">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--kt-yellow)] text-[var(--kt-ink)]">
                                    <Car className="h-6 w-6" strokeWidth={2.5} />
                                </span>
                                <div>
                                    <div className="font-display text-lg font-bold">
                                        Köşe Taksi Durağı
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-[var(--kt-yellow)]">
                                        Yeniçiftlik · Marmaraereğlisi
                                    </div>
                                </div>
                            </div>

                            <div className="checker-thin my-5 h-2 w-full rounded-sm" />

                            <ul className="space-y-3 text-sm">
                                <li className="flex items-start gap-3">
                                    <Clock className="mt-0.5 h-4 w-4 text-[var(--kt-yellow)]" />
                                    <span><strong>7/24</strong> ulaşılabilir · Gece transferleri dahil</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <MapPin className="mt-0.5 h-4 w-4 text-[var(--kt-yellow)]" />
                                    <span>Yeniçiftlik merkezli · İstanbul / Tekirdağ / Çorlu hatları</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Shield className="mt-0.5 h-4 w-4 text-[var(--kt-yellow)]" />
                                    <span>Lisanslı şoförler · Temiz ve bakımlı araç</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Star className="mt-0.5 h-4 w-4 text-[var(--kt-yellow)]" />
                                    <span>Sabit ve uygun ücret · Önceden bilgi</span>
                                </li>
                            </ul>

                            <button
                                onClick={call}
                                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--kt-yellow)] py-3 font-bold text-[var(--kt-ink)] transition hover:brightness-110"
                            >
                                <Phone className="h-4 w-4" strokeWidth={2.6} />
                                {PHONE_DISPLAY}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative border-t-2 border-[var(--kt-yellow)]/30 bg-[var(--kt-ink)] py-3 overflow-hidden">
                    <div className="marquee-track flex w-max gap-10 whitespace-nowrap text-sm font-semibold uppercase tracking-widest text-[var(--kt-yellow)]">
                        {[...marqueeRoutes, ...marqueeRoutes].map((r, i) => (
                            <span key={i} className="inline-flex items-center gap-3">
                                <ChevronRight className="h-4 w-4" />
                                {r}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section id="services" className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
                <div className="mb-12 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--kt-ink-soft)]">
                            / Hizmetlerimiz
                        </span>
                        <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
                            Her güzergâh, her saat <br className="hidden sm:block" />
                            <span className="bg-[var(--kt-yellow)] px-2">tek bir aramada</span>.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm text-[var(--kt-ink-soft)] sm:text-base">
                        Yolcu, kurumsal ve özel transfer ihtiyaçlarınız için Yeniçiftlik Köşe Taksi yanınızda.
                    </p>
                </div>

                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {services.map(({ icon: Icon, title, desc }, idx) => (
                        <article
                            key={title}
                            data-testid={`${TAXI.serviceCard}-${idx}`}
                            className="kt-card relative rounded-2xl p-6"
                        >
                            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--kt-yellow)] border-2 border-[var(--kt-ink)]">
                                <Icon className="h-6 w-6" strokeWidth={2.4} />
                            </span>
                            <h3 className="font-display mt-5 text-xl font-extrabold">
                                {title}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-[var(--kt-ink-soft)]">
                                {desc}
                            </p>
                        </article>
                    ))}
                </div>
            </section>

            <div className="kt-divider" />

            {/* Areas */}
            <section id="areas" className="bg-[var(--kt-ink)] py-20 text-[var(--kt-paper)] sm:py-24">
                <div className="mx-auto max-w-6xl px-5 sm:px-8">
                    <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--kt-yellow)]">
                                / Hizmet Bölgeleri
                            </span>
                            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
                                Yeniçiftlik'ten <br className="hidden sm:block" />
                                <span className="text-[var(--kt-yellow)]">
                                    İstanbul · Tekirdağ · Çorlu
                                </span>{" "}
                                hatları.
                            </h2>
                        </div>
                        <p className="max-w-sm text-sm text-[var(--kt-paper)]/70 sm:text-base">
                            Marmara Bölgesi'nde sıkça istenen güzergâhların tamamına 7/24 hizmet veriyoruz.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        {areas.map((a, i) => (
                            <span
                                key={a}
                                data-testid={`${TAXI.areaChip}-${i}`}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--kt-yellow)]/40 bg-white/5 px-4 py-2 text-sm font-semibold text-[var(--kt-paper)] backdrop-blur-sm transition hover:border-[var(--kt-yellow)] hover:bg-[var(--kt-yellow)] hover:text-[var(--kt-ink)]"
                            >
                                <MapPin className="h-3.5 w-3.5" />
                                {a}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* About */}
            <section id="about" className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
                <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--kt-ink-soft)]">
                            / Hakkımızda
                        </span>
                        <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
                            Köşe başında,
                            <br />
                            <span className="bg-[var(--kt-yellow)] px-2">her zaman hazır</span>.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-[var(--kt-ink-soft)] sm:text-lg">
                            <strong>Yeniçiftlik Köşe Taksi</strong>, yıllardır Marmaraereğlisi Yeniçiftlik'te
                            yolcularını güvenle, vaktinde ve sıcacık bir karşılamayla taşıyor. İstanbul,
                            Tekirdağ ve Çorlu hatları başta olmak üzere kısa şehir içi yolculuklardan uzun
                            transferlere kadar 7/24 hizmet veriyoruz.
                        </p>
                        <ul className="mt-6 space-y-3 text-sm sm:text-base">
                            <li className="flex items-start gap-3">
                                <span className="mt-1 h-2 w-2 rounded-full bg-[var(--kt-yellow)] ring-2 ring-[var(--kt-ink)]" />
                                Lisanslı, deneyimli ve güler yüzlü şoför kadrosu
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-1 h-2 w-2 rounded-full bg-[var(--kt-yellow)] ring-2 ring-[var(--kt-ink)]" />
                                Düzenli bakımı yapılmış, temiz ve konforlu araçlar
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-1 h-2 w-2 rounded-full bg-[var(--kt-yellow)] ring-2 ring-[var(--kt-ink)]" />
                                Önceden bildirilen, şeffaf ve uygun ücretlendirme
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-1 h-2 w-2 rounded-full bg-[var(--kt-yellow)] ring-2 ring-[var(--kt-ink)]" />
                                Gecenin her saatinde ulaşılabilir telefon hattı
                            </li>
                        </ul>
                    </div>

                    <div className="relative">
                        <div className="checker absolute -inset-3 -z-10 rounded-3xl opacity-90" />
                        <div className="relative rounded-3xl border-2 border-[var(--kt-ink)] bg-[var(--kt-paper)] p-7 shadow-[10px_10px_0_0_var(--kt-ink)]">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-widest text-[var(--kt-ink-soft)]">
                                    Bize Ulaşın
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--kt-ink)] px-3 py-1 text-xs font-bold uppercase text-[var(--kt-yellow)]">
                                    <Clock className="h-3 w-3" /> 7/24
                                </span>
                            </div>
                            <a
                                href={`tel:${PHONE_TEL}`}
                                className="font-display mt-4 block text-4xl font-extrabold leading-tight sm:text-5xl"
                            >
                                {PHONE_DISPLAY}
                            </a>
                            <p className="mt-2 text-sm text-[var(--kt-ink-soft)]">
                                Yeniçiftlik Mah., Marmaraereğlisi / Tekirdağ
                            </p>
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <button
                                    onClick={call}
                                    className="btn-yellow flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold"
                                >
                                    <Phone className="h-4 w-4" /> Ara
                                </button>
                                <a
                                    href={`https://wa.me/${PHONE_WA}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-ghost flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold"
                                >
                                    <MessageCircle className="h-4 w-4" /> WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Location / Map */}
            <section id="location" className="bg-[var(--kt-paper)] pb-20">
                <div className="mx-auto max-w-6xl px-5 sm:px-8">
                    <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--kt-ink-soft)]">
                                / Konum
                            </span>
                            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
                                Yeniçiftlik'in tam{" "}
                                <span className="bg-[var(--kt-yellow)] px-2">köşesinde</span>.
                            </h2>
                        </div>
                        <a
                            data-testid={TAXI.mapOpenBtn}
                            href="https://www.google.com/maps/search/?api=1&query=Yeni%C3%A7iftlik+K%C3%B6%C5%9Fe+Taksi+Marmaraere%C4%9Flisi+Tekirda%C4%9F"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-yellow inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"
                        >
                            <Navigation className="h-4 w-4" /> Yol Tarifi Al
                        </a>
                    </div>

                    <div
                        data-testid={TAXI.mapEmbed}
                        className="overflow-hidden rounded-2xl border-2 border-[var(--kt-ink)] shadow-[10px_10px_0_0_var(--kt-ink)]"
                    >
                        <iframe
                            title="Yeniçiftlik Köşe Taksi konum"
                            src="https://www.google.com/maps?q=Yeniçiftlik,Marmaraereğlisi,Tekirdağ&hl=tr&z=14&output=embed"
                            width="100%"
                            height="420"
                            style={{ border: 0, display: "block" }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                        />
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section id="contact" className="relative overflow-hidden bg-[var(--kt-yellow)] py-20 sm:py-24">
                <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rotate-12 checker opacity-30" />
                <div className="pointer-events-none absolute -right-10 -bottom-10 h-40 w-40 -rotate-12 checker opacity-30" />

                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--kt-ink)]/70">
                        / İletişim
                    </span>
                    <h2 className="font-display mx-auto mt-3 max-w-3xl text-3xl font-extrabold leading-tight text-[var(--kt-ink)] sm:text-5xl lg:text-6xl">
                        Bir telefon yeter.
                        <br />
                        Köşe Taksi yolda.
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-base text-[var(--kt-ink)]/80 sm:text-lg">
                        7/24 aktif hattımızdan rezervasyon ve transfer için bizi arayabilirsiniz.
                    </p>

                    <button
                        data-testid={TAXI.contactCallBtn}
                        onClick={call}
                        className="mt-8 inline-flex items-center gap-3 rounded-full bg-[var(--kt-ink)] px-7 py-4 text-lg font-extrabold text-[var(--kt-yellow)] transition hover:translate-y-[-2px] hover:shadow-[0_10px_0_0_rgba(0,0,0,0.25)]"
                    >
                        <Phone className="h-5 w-5" strokeWidth={2.6} />
                        {PHONE_DISPLAY}
                    </button>

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-[var(--kt-ink)]/80">
                        <span className="inline-flex items-center gap-2">
                            <Clock className="h-4 w-4" /> 7/24 Hizmet
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <MapPin className="h-4 w-4" /> Yeniçiftlik · Marmaraereğlisi
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <Shield className="h-4 w-4" /> Lisanslı Şoförler
                        </span>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[var(--kt-ink)] text-[var(--kt-paper)]">
                <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:grid-cols-3 sm:px-8">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--kt-yellow)] text-[var(--kt-ink)]">
                                <Car className="h-5 w-5" strokeWidth={2.5} />
                            </span>
                            <div className="font-display text-base font-extrabold leading-tight">
                                Yeniçiftlik
                                <span className="block text-[10px] font-semibold uppercase tracking-widest text-[var(--kt-yellow)]">
                                    Köşe Taksi
                                </span>
                            </div>
                        </div>
                        <p className="mt-4 text-sm text-[var(--kt-paper)]/70">
                            Marmaraereğlisi Yeniçiftlik merkezli, 7/24 güvenli ve hızlı taksi hizmeti.
                            İstanbul · Tekirdağ · Çorlu hatlarında yanınızdayız.
                        </p>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[var(--kt-yellow)]">
                            Hızlı Bağlantılar
                        </div>
                        <ul className="mt-4 space-y-2 text-sm">
                            <li><a className="hover:text-[var(--kt-yellow)]" href="#services">Hizmetler</a></li>
                            <li><a className="hover:text-[var(--kt-yellow)]" href="#areas">Bölgeler</a></li>
                            <li><a className="hover:text-[var(--kt-yellow)]" href="#about">Hakkımızda</a></li>
                            <li><a className="hover:text-[var(--kt-yellow)]" href="#location">Konum</a></li>
                            <li><a className="hover:text-[var(--kt-yellow)]" href="#contact">İletişim</a></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[var(--kt-yellow)]">
                            İletişim
                        </div>
                        <ul className="mt-4 space-y-2 text-sm">
                            <li className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-[var(--kt-yellow)]" />
                                <a href={`tel:${PHONE_TEL}`} className="font-bold">{PHONE_DISPLAY}</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-[var(--kt-yellow)]" />
                                7 gün 24 saat
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin className="mt-0.5 h-4 w-4 text-[var(--kt-yellow)]" />
                                Yeniçiftlik Mah., Marmaraereğlisi / Tekirdağ
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-white/10">
                    <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-5 py-5 text-xs text-[var(--kt-paper)]/60 sm:flex-row sm:px-8">
                        <span data-testid={TAXI.footerYear}>
                            © {year} Yeniçiftlik Köşe Taksi · Tüm hakları saklıdır.
                        </span>
                        <span>Marmaraereğlisi · Tekirdağ · 7/24 Taksi</span>
                    </div>
                </div>
            </footer>

            {/* Floating Phone CTA (bottom-right) */}
            <a
                href={`tel:${PHONE_TEL}`}
                data-testid={TAXI.floatingCallBtn}
                aria-label={`Köşe Taksi'yi ara: ${PHONE_DISPLAY}`}
                className="kt-fab fixed bottom-5 right-5 z-50 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[var(--kt-ink)] bg-[var(--kt-yellow)] text-[var(--kt-ink)] transition sm:bottom-7 sm:right-7 sm:h-[72px] sm:w-[72px]"
            >
                <Phone className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.6} />
                <span className="sr-only">Hemen ara {PHONE_DISPLAY}</span>
            </a>
        </div>
    );
}
